// Acomodo de los nodos del grafo. Calculo puro, sin DOM: entra una lista de
// nodos y aristas, salen coordenadas.
//
// Se dibuja a mano en vez de sumar una libreria: la mas liviana del rubro pesa
// mas del doble que toda la app junta (PLAN-06, seccion 1).
//
// Determinista a proposito: las posiciones iniciales salen del indice del nodo,
// nunca de Math.random(). El mismo grafo se dibuja siempre igual y los tests
// pueden comprobar el resultado.

export interface NodoGrafo {
  id: string; // 'proyecto:3'
  tipo: string;
  etiqueta: string;
  hash: string | null; // a donde lleva al tocarlo
  estado?: string | null; // ya resuelto por la API: VENCIDA, BLOQUEADA, ENVIADO...
}

export interface AristaGrafo {
  origen: string;
  destino: string;
  tipo: string;
}

export interface Posicion {
  x: number;
  y: number;
}

export interface Acomodo {
  posiciones: Map<string, Posicion>;
  ancho: number;
  alto: number;
}

const ITERACIONES = 300;
const LARGO_ARISTA = 90;
const REPULSION = 9000;
const MARGEN = 40;

/** La arista que ordena el trabajo: la de origen va antes que la de destino. */
export const ARISTA_ORDEN = "BLOQUEA";

/** Cuanto tiene que bajar la frenada respecto de la que la frena. */
const SALTO_NIVEL = 70;

/** Posición inicial en espiral: reparte los nodos sin superponerlos. */
function inicial(i: number): Posicion {
  const angulo = i * 2.399963; // ángulo áureo, evita que se alineen
  const radio = 24 * Math.sqrt(i + 1);
  return { x: Math.cos(angulo) * radio, y: Math.sin(angulo) * radio };
}

/**
 * Acomoda los nodos con fuerzas simples: los unidos se atraen, todos se
 * repelen. Se corre una sola vez, al cargar; no queda nada animado, que es lo
 * que traba un celular.
 */
export function acomodar(nodos: NodoGrafo[], aristas: AristaGrafo[]): Acomodo {
  const posiciones = new Map<string, Posicion>();
  nodos.forEach((n, i) => posiciones.set(n.id, inicial(i)));

  if (nodos.length === 0) return { posiciones, ancho: MARGEN * 2, alto: MARGEN * 2 };

  // Sólo las aristas cuyos dos extremos existen.
  const validas = aristas.filter((a) => posiciones.has(a.origen) && posiciones.has(a.destino));

  for (let paso = 0; paso < ITERACIONES; paso++) {
    // Va bajando: primero acomoda grueso, después afina.
    const enfriado = 1 - paso / ITERACIONES;

    const fuerzas = new Map<string, Posicion>();
    for (const n of nodos) fuerzas.set(n.id, { x: 0, y: 0 });

    // Repulsión de todos contra todos.
    for (let i = 0; i < nodos.length; i++) {
      for (let j = i + 1; j < nodos.length; j++) {
        const a = posiciones.get(nodos[i].id) as Posicion;
        const b = posiciones.get(nodos[j].id) as Posicion;
        let dx = a.x - b.x;
        let dy = a.y - b.y;
        let dist2 = dx * dx + dy * dy;
        if (dist2 < 0.01) {
          // Dos nodos exactamente encima: se los separa a mano, si no la
          // división de abajo da infinito.
          dx = (i % 2 === 0 ? 1 : -1) * 0.5;
          dy = 0.5;
          dist2 = dx * dx + dy * dy;
        }
        const f = REPULSION / dist2;
        const dist = Math.sqrt(dist2);
        const fa = fuerzas.get(nodos[i].id) as Posicion;
        const fb = fuerzas.get(nodos[j].id) as Posicion;
        fa.x += (dx / dist) * f;
        fa.y += (dy / dist) * f;
        fb.x -= (dx / dist) * f;
        fb.y -= (dy / dist) * f;
      }
    }

    // Atracción por las aristas.
    for (const a of validas) {
      const o = posiciones.get(a.origen) as Posicion;
      const d = posiciones.get(a.destino) as Posicion;
      const dx = d.x - o.x;
      const dy = d.y - o.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const f = (dist - LARGO_ARISTA) * 0.05;
      const fo = fuerzas.get(a.origen) as Posicion;
      const fd = fuerzas.get(a.destino) as Posicion;
      fo.x += (dx / dist) * f;
      fo.y += (dy / dist) * f;
      fd.x -= (dx / dist) * f;
      fd.y -= (dy / dist) * f;
    }

    // Orden de trabajo: en las aristas de bloqueo, la que frena tira para
    // arriba y la frenada para abajo. Sin esto el par queda acostado al azar y
    // el dibujo no dice cual va primero, que es justo lo que hay que leer de un
    // vistazo. Es un empujon suave: no arma niveles rigidos, solo inclina.
    for (const a of validas) {
      if (a.tipo !== ARISTA_ORDEN) continue;
      const o = posiciones.get(a.origen) as Posicion;
      const d = posiciones.get(a.destino) as Posicion;
      const falta = o.y + SALTO_NIVEL - d.y;
      if (falta <= 0) continue; // ya esta bien puesta
      const fo = fuerzas.get(a.origen) as Posicion;
      const fd = fuerzas.get(a.destino) as Posicion;
      fo.y -= falta * 0.25;
      fd.y += falta * 0.25;
    }

    for (const n of nodos) {
      const p = posiciones.get(n.id) as Posicion;
      const f = fuerzas.get(n.id) as Posicion;
      // Tope por paso: sin esto, dos nodos muy juntos salen disparados.
      const tope = 24 * enfriado;
      const largo = Math.sqrt(f.x * f.x + f.y * f.y) || 1;
      const escala = Math.min(largo, tope) / largo;
      p.x += f.x * escala;
      p.y += f.y * escala;
    }
  }

  // Se corre todo al origen y se mide, para que el SVG sepa su tamaño.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of posiciones.values()) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  for (const p of posiciones.values()) {
    p.x = Math.round((p.x - minX + MARGEN) * 100) / 100;
    p.y = Math.round((p.y - minY + MARGEN) * 100) / 100;
  }

  return {
    posiciones,
    ancho: Math.round(maxX - minX + MARGEN * 2),
    alto: Math.round(maxY - minY + MARGEN * 2),
  };
}

export interface Secuencia {
  antes: string[]; // hay que terminar estas para poder con la elegida
  despues: string[]; // estas se destraban cuando la elegida se termina
}

/**
 * Que va antes y que va despues de un nodo, siguiendo las aristas de bloqueo.
 *
 * Un solo salto: contesta "primero esto, entonces aquello", que es como se
 * decide que hacer ahora. La cadena entera se recorre tocando de a un nodo.
 */
export function secuencia(aristas: AristaGrafo[], id: string): Secuencia {
  const antes: string[] = [];
  const despues: string[] = [];
  for (const a of aristas) {
    if (a.tipo !== ARISTA_ORDEN) continue;
    if (a.destino === id && !antes.includes(a.origen)) antes.push(a.origen);
    if (a.origen === id && !despues.includes(a.destino)) despues.push(a.destino);
  }
  return { antes, despues };
}
